import React, { useState, useEffect, useCallback } from 'react';
import {
  Search, MessageSquare, Clock, AlertCircle, CheckCircle, XCircle,
  User, Calendar, Tag, ChevronLeft, ChevronRight, X, Send, ArrowLeft, Shield,
} from 'lucide-react';
import { ticketService } from '../services/ticketService';

const STATUS_CONFIG = {
  OPEN: { label: 'Open', icon: AlertCircle, color: 'bg-blue-100 text-blue-700' },
  IN_PROGRESS: { label: 'In Progress', icon: Clock, color: 'bg-yellow-100 text-yellow-700' },
  RESOLVED: { label: 'Resolved', icon: CheckCircle, color: 'bg-green-100 text-green-700' },
  CLOSED: { label: 'Closed', icon: XCircle, color: 'bg-gray-100 text-gray-700' },
};

const PRIORITY_CONFIG = {
  HIGH: { label: 'High', color: 'bg-red-100 text-red-700' },
  MEDIUM: { label: 'Medium', color: 'bg-yellow-100 text-yellow-700' },
  LOW: { label: 'Low', color: 'bg-green-100 text-green-700' },
};

const SupportTickets = () => {
  // List view state
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({ open: 0, inProgress: 0, resolved: 0, closed: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  // Detail view state
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [replyMessage, setReplyMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const search = searchTerm.trim() || undefined;
      const data = await ticketService.getAllTickets(statusFilter, search, currentPage, 10);
      setTickets(data.content || []);
      setTotalPages(data.totalPages || 0);
      setTotalElements(data.totalElements || 0);
    } catch (err) {
      console.error('Failed to load tickets:', err);
      setTickets([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, searchTerm, currentPage]);

  const fetchStats = useCallback(async () => {
    try {
      const data = await ticketService.getStats();
      setStats(data);
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  }, []);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  useEffect(() => {
    const timer = setTimeout(() => setCurrentPage(0), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const openTicketDetail = async (ticketId) => {
    setDetailLoading(true);
    try {
      const data = await ticketService.getTicketDetail(ticketId);
      setSelectedTicket(data);
    } catch (err) {
      console.error('Failed to load ticket detail:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleSendReply = async (e) => {
    e.preventDefault();
    if (!replyMessage.trim() || !selectedTicket) return;
    setSending(true);
    try {
      await ticketService.addReply(selectedTicket.id, replyMessage.trim());
      setReplyMessage('');
      // Refresh the detail
      const data = await ticketService.getTicketDetail(selectedTicket.id);
      setSelectedTicket(data);
      fetchTickets();
    } catch (err) {
      console.error('Failed to send reply:', err);
    } finally {
      setSending(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!selectedTicket) return;
    setUpdatingStatus(true);
    try {
      await ticketService.updateStatus(selectedTicket.id, newStatus);
      const data = await ticketService.getTicketDetail(selectedTicket.id);
      setSelectedTicket(data);
      fetchTickets();
      fetchStats();
    } catch (err) {
      console.error('Failed to update status:', err);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const StatusBadge = ({ status }) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.OPEN;
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3.5 h-3.5" />
        {config.label}
      </span>
    );
  };

  const PriorityBadge = ({ priority }) => {
    const config = PRIORITY_CONFIG[priority] || PRIORITY_CONFIG.MEDIUM;
    return (
      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  // ============ Detail View ============
  if (selectedTicket) {
    const statusConfig = STATUS_CONFIG[selectedTicket.status] || STATUS_CONFIG.OPEN;
    const StatusIcon = statusConfig.icon;

    return (
      <div className="space-y-6">
        <button
          onClick={() => { setSelectedTicket(null); setReplyMessage(''); }}
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Tickets
        </button>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Header */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <span className="text-sm text-gray-500 font-mono">{selectedTicket.ticketNumber}</span>
                  <h1 className="text-2xl font-bold text-gray-900 mt-1">{selectedTicket.title}</h1>
                </div>
                <span className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium ${statusConfig.color}`}>
                  <StatusIcon className="w-4 h-4" />
                  {statusConfig.label}
                </span>
              </div>
              {selectedTicket.description && (
                <p className="text-gray-700 whitespace-pre-wrap">{selectedTicket.description}</p>
              )}
            </div>

            {/* Conversation */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="p-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Conversation</h2>
              </div>
              <div className="p-4 space-y-4 max-h-[500px] overflow-y-auto">
                {(!selectedTicket.replies || selectedTicket.replies.length === 0) ? (
                  <div className="text-center py-8 text-gray-500">
                    <p>No replies yet.</p>
                  </div>
                ) : (
                  selectedTicket.replies.map((reply) => (
                    <div
                      key={reply.id}
                      className={`flex ${reply.isAdminReply ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className="max-w-[80%]">
                        <div className={`flex items-center gap-2 mb-1 ${reply.isAdminReply ? 'justify-end' : ''}`}>
                          {reply.isAdminReply ? (
                            <Shield className="w-4 h-4 text-primary-600" />
                          ) : (
                            <User className="w-4 h-4 text-gray-400" />
                          )}
                          <span className="text-xs font-medium text-gray-600">
                            {reply.repliedByName || reply.repliedByEmail || 'Unknown'}
                          </span>
                          <span className="text-xs text-gray-400">
                            {reply.createdAt ? new Date(reply.createdAt).toLocaleString() : ''}
                          </span>
                        </div>
                        <div
                          className={`rounded-xl px-4 py-3 ${
                            reply.isAdminReply
                              ? 'bg-primary-50 border border-primary-100 text-gray-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          <p className="text-sm whitespace-pre-wrap">{reply.message}</p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Reply Input */}
              <div className="p-4 border-t border-gray-200">
                <form onSubmit={handleSendReply} className="flex gap-3">
                  <input
                    type="text"
                    value={replyMessage}
                    onChange={(e) => setReplyMessage(e.target.value)}
                    placeholder="Type your reply as admin..."
                    className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <button
                    type="submit"
                    disabled={!replyMessage.trim() || sending}
                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
                  >
                    <Send className="w-4 h-4" />
                    {sending ? 'Sending...' : 'Reply'}
                  </button>
                </form>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Details Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Details</h3>
              <div className="space-y-4">
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase mb-1">Priority</p>
                  <PriorityBadge priority={selectedTicket.priority} />
                </div>
                {selectedTicket.serviceType && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase mb-1">Service</p>
                    <span className="text-sm font-medium text-gray-900 bg-gray-100 px-2.5 py-1 rounded">
                      {selectedTicket.serviceType}
                    </span>
                  </div>
                )}
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase mb-1">Category</p>
                  <span className="inline-flex items-center gap-1 text-sm text-gray-700">
                    <Tag className="w-4 h-4" />
                    {selectedTicket.category ? selectedTicket.category.replace('_', ' ') : 'Other'}
                  </span>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase mb-1">Company</p>
                  <span className="text-sm text-gray-700">{selectedTicket.companyName || 'N/A'}</span>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase mb-1">Created By</p>
                  <span className="text-sm text-gray-700">
                    {selectedTicket.createdByName || selectedTicket.createdByEmail}
                  </span>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase mb-1">Created</p>
                  <span className="inline-flex items-center gap-1 text-sm text-gray-700">
                    <Calendar className="w-4 h-4" />
                    {selectedTicket.createdAt ? new Date(selectedTicket.createdAt).toLocaleString() : ''}
                  </span>
                </div>
              </div>
            </div>

            {/* Status Change Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Update Status</h3>
              <div className="space-y-2">
                {Object.entries(STATUS_CONFIG).map(([key, config]) => {
                  const Icon = config.icon;
                  const isActive = selectedTicket.status === key;
                  return (
                    <button
                      key={key}
                      onClick={() => !isActive && handleStatusChange(key)}
                      disabled={isActive || updatingStatus}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        isActive
                          ? `${config.color} cursor-default`
                          : 'text-gray-600 hover:bg-gray-50 border border-gray-200'
                      } disabled:opacity-70`}
                    >
                      <Icon className="w-4 h-4" />
                      {config.label}
                      {isActive && <span className="ml-auto text-xs">(Current)</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ============ List View ============
  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Support Tickets</h1>
        <p className="text-gray-600 mt-1">Manage customer support requests</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Open', value: stats.open, color: 'text-blue-600', bg: 'bg-blue-50', icon: AlertCircle },
          { label: 'In Progress', value: stats.inProgress, color: 'text-yellow-600', bg: 'bg-yellow-50', icon: Clock },
          { label: 'Resolved', value: stats.resolved, color: 'text-green-600', bg: 'bg-green-50', icon: CheckCircle },
          { label: 'Total', value: stats.total, color: 'text-gray-600', bg: 'bg-gray-50', icon: MessageSquare },
        ].map((stat) => (
          <div key={stat.label} className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 ${stat.bg} rounded-lg flex items-center justify-center`}>
                <stat.icon className={`w-5 h-5 ${stat.color}`} />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                <p className="text-sm text-gray-600">{stat.label}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Search & Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by ticket number, title, or company..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(0); }}
            className="px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="all">All Status</option>
            <option value="OPEN">Open</option>
            <option value="IN_PROGRESS">In Progress</option>
            <option value="RESOLVED">Resolved</option>
            <option value="CLOSED">Closed</option>
          </select>
        </div>
      </div>

      {/* Tickets List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : tickets.length === 0 ? (
          <div className="text-center py-16">
            <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">No tickets found</h3>
            <p className="text-gray-600">No support tickets match your filters.</p>
          </div>
        ) : (
          <>
            <div className="divide-y divide-gray-200">
              {tickets.map((ticket) => (
                <div
                  key={ticket.id}
                  onClick={() => openTicketDetail(ticket.id)}
                  className="p-6 hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <span className="text-sm font-medium text-gray-500 font-mono">{ticket.ticketNumber}</span>
                        <StatusBadge status={ticket.status} />
                        <PriorityBadge priority={ticket.priority} />
                        {ticket.serviceType && (
                          <span className="text-xs font-medium text-gray-600 bg-gray-100 px-2 py-1 rounded">
                            {ticket.serviceType}
                          </span>
                        )}
                        {ticket.category && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
                            <Tag className="w-3 h-3" />
                            {ticket.category.replace('_', ' ')}
                          </span>
                        )}
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">{ticket.title}</h3>
                      <div className="flex items-center gap-4 text-sm text-gray-600 flex-wrap">
                        <div className="flex items-center gap-1">
                          <User className="w-4 h-4" />
                          {ticket.createdByName || ticket.createdByEmail} ({ticket.companyName || 'N/A'})
                        </div>
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {ticket.createdAt ? new Date(ticket.createdAt).toLocaleString() : ''}
                        </div>
                        <div className="flex items-center gap-1">
                          <MessageSquare className="w-4 h-4" />
                          {ticket.replyCount || 0} replies
                        </div>
                      </div>
                    </div>
                    {ticket.updatedAt && (
                      <div className="text-right ml-4">
                        <div className="text-xs text-gray-500">Updated</div>
                        <div className="text-sm text-gray-700">{new Date(ticket.updatedAt).toLocaleDateString()}</div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200">
                <p className="text-sm text-gray-600">
                  Showing {currentPage * 10 + 1} to {Math.min((currentPage + 1) * 10, totalElements)} of {totalElements} tickets
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                    disabled={currentPage === 0}
                    className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <span className="text-sm text-gray-700">
                    Page {currentPage + 1} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                    disabled={currentPage >= totalPages - 1}
                    className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Detail Loading Overlay */}
      {detailLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="w-12 h-12 border-4 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}
    </div>
  );
};

export default SupportTickets;
